import { useNavigate, useLocation } from "react-router-dom";
import { Home, TrendingUp, Wallet, Gift, ScanLine } from "lucide-react";

const NavBar = ({ active }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from prop or current path
  const getActiveTab = () => {
    if (active) return active;
    const path = location.pathname;
    if (path === "/dashboard" || path === "/") return "home";
    if (path === "/grow") return "grow";
    if (path === "/scan") return "scan";
    if (path === "/explore" || path === "/coach") return "wallet";
    if (path.startsWith("/lifestyle") || path.startsWith("/events") || path === "/my-tickets") return "lifestyle";
    return "home";
  };

  const currentActive = getActiveTab();

  // Left tabs (before center button)
  const leftTabs = [
    { id: "home", icon: Home, label: "Home", path: "/dashboard" },
    { id: "wallet", icon: Wallet, label: "Wallet", path: "/explore" },
  ];

  // Right tabs (after center button)
  const rightTabs = [
    { id: "grow", icon: TrendingUp, label: "Grow", path: "/grow" },
    { id: "lifestyle", icon: Gift, label: "Lifestyle", path: "/lifestyle" },
  ];

  const TabButton = ({ tab }) => {
    const Icon = tab.icon;
    const isActive = currentActive === tab.id;
    
    return (
      <button
        data-testid={`nav-${tab.id}`}
        onClick={() => navigate(tab.path)}
        className="flex-1 flex flex-col items-center justify-center gap-1 min-w-0"
      >
        <Icon 
          className={`w-6 h-6 transition-colors ${
            isActive ? "text-teal-400" : "text-zinc-500"
          }`}
          strokeWidth={isActive ? 2 : 1.5}
        />
        <span 
          className={`text-[11px] font-medium transition-colors ${
            isActive ? "text-teal-400" : "text-zinc-500"
          }`}
        >
          {tab.label}
        </span>
      </button>
    );
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40" data-testid="bottom-nav">
      {/* Floating center scan button - positioned above the nav bar */}
      <div className="absolute left-1/2 -translate-x-1/2 -top-7 z-10">
        <button
          data-testid="nav-scan"
          onClick={() => navigate("/scan")}
          className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all ${
            currentActive === "scan"
              ? "bg-gradient-to-br from-teal-400 to-cyan-500 shadow-teal-500/40"
              : "bg-gradient-to-br from-teal-500 to-cyan-500 shadow-teal-500/30 hover:shadow-teal-500/50"
          }`}
          style={{
            boxShadow: currentActive === "scan" 
              ? "0 8px 24px rgba(20, 184, 166, 0.5)" 
              : "0 8px 24px rgba(20, 184, 166, 0.4)"
          }}
        >
          <ScanLine className="w-7 h-7 text-white" strokeWidth={2} />
        </button>
      </div>

      {/* Navigation bar with curved notch */}
      <nav className="relative">
        {/* SVG curved background */}
        <svg 
          className="absolute bottom-0 left-0 right-0 w-full" 
          height="80" 
          viewBox="0 0 390 80" 
          preserveAspectRatio="none"
          fill="none"
        >
          {/* Main bar shape with center notch */}
          <path
            d="M0 20 
               L145 20 
               Q155 20 160 28
               Q175 52 195 52
               Q215 52 230 28
               Q235 20 245 20
               L390 20 
               L390 80 
               L0 80 
               Z"
            fill="#111118"
          />
          {/* Top border line following the curve */}
          <path
            d="M0 20 
               L145 20 
               Q155 20 160 28
               Q175 52 195 52
               Q215 52 230 28
               Q235 20 245 20
               L390 20"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
            fill="none"
          />
        </svg>

        {/* Tab buttons container */}
        <div className="relative h-20 flex items-end pb-2">
          <div className="w-full max-w-lg mx-auto flex items-end px-2">
            {/* Left tabs */}
            <div className="flex flex-1 justify-around pb-1">
              {leftTabs.map((tab) => (
                <TabButton key={tab.id} tab={tab} />
              ))}
            </div>

            {/* Center spacer for the floating button */}
            <div className="w-20 flex-shrink-0" />

            {/* Right tabs */}
            <div className="flex flex-1 justify-around pb-1">
              {rightTabs.map((tab) => (
                <TabButton key={tab.id} tab={tab} />
              ))}
            </div>
          </div>
        </div>

        {/* Safe area spacer for iOS */}
        <div 
          className="bg-[#111118]" 
          style={{ height: "env(safe-area-inset-bottom, 0px)" }} 
        />
      </nav>
    </div>
  );
};

export default NavBar;
